import { Injectable, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Database, ref, listVal, objectVal } from "@angular/fire/database";

/**
 * App-wide config (subscription plans, tax rates, admins, coupons, min client
 * version) as a **signal store** on the modular Firebase SDK.
 *
 * First store of the Phase 2 Signals migration — replaces the legacy pattern
 * (compat `valueChanges()` observables self-subscribed into `latest*` plain
 * fields). The config paths are always-on (no auth/path keying), so plain
 * `toSignal(listVal/objectVal(ref(...)))` is all that's needed. The helper
 * methods keep their original signatures (callers unchanged) and just read the
 * signals instead of the old `latest*` mirrors.
 */
@Injectable({ providedIn: "root" })
export class ConfigService {
  private readonly db = inject(Database);

  readonly admins = toSignal(listVal<any>(ref(this.db, "config/admins")), {
    initialValue: [] as any[],
  });
  readonly plans = toSignal(listVal<any>(ref(this.db, "config/plans")), {
    initialValue: [] as any[],
  });
  readonly rates = toSignal(listVal<any>(ref(this.db, "config/rates")), {
    initialValue: [] as any[],
  });
  readonly coupons = toSignal(objectVal<any>(ref(this.db, "config/coupons")), {
    initialValue: null as any,
  });
  /**
   * Min client build version. app.component force-reloads when this exceeds
   * environment.appVersion (the deploy gate).
   */
  readonly minClientVersion = toSignal(
    objectVal<number | null>(ref(this.db, "config/minClientVersion")),
    { initialValue: null as number | null }
  );

  isAdmin(uid: string): boolean {
    return (this.admins() ?? []).some((a) => a && a.uid === uid);
  }

  getPlan(planID: string) {
    return (this.plans() ?? []).find((p) => p && p.code === planID) ?? null;
  }

  getPlanIndex(planID: string) {
    return (this.plans() ?? []).findIndex((p) => p && p.code === planID);
  }

  getPlanName(planID?: string) {
    if (planID === "ANTE") return "Ante";
    const plan = planID ? this.getPlan(planID) : this.getPlan("KITCHEN_TABLE");
    return plan ? plan.name : null;
  }

  getPlanByIndex(index: number) {
    return (this.plans() ?? [])[index] ?? null;
  }

  getPlayerLimit(planID: string) {
    if (planID === "ANTE") return -1;
    const plan = this.getPlan(planID);
    return plan ? plan.player_limit : undefined;
  }

  getCoupon(id: string) {
    const coupons = this.coupons();
    return coupons ? coupons[id.toUpperCase()] : null;
  }
}
